import {
  batchReplaceCustomerContacts,
  type CustomerContactBatchResult,
} from '@/services/customerContactBatch';
import { Alert, App, Form, Input, List, Tabs } from 'antd';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import ConfigExcelImportPanel from './ConfigExcelImportPanel';
import { CUSTOMER_CONFIG_EXCEL } from './excelCodes';
import type {
  BatchScenarioPanelHandle,
  BatchScenarioPanelProps,
} from './types';
import { filterValidCustomers } from './types';

/** 客户方联系人批量修改；当前每个客户配置一位联系人，Excel每个客户填写一行。 */
const CustomerContactBatchPanel = forwardRef<
  BatchScenarioPanelHandle,
  BatchScenarioPanelProps
>(({ customers, active, onDirty, onFooterChange }, ref) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [mode, setMode] = useState('uniform');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<CustomerContactBatchResult>();

  useEffect(() => {
    if (!active) return;
    form.resetFields();
    setMode('uniform');
    setResult(undefined);
  }, [active, form]);

  useEffect(() => {
    if (active) onFooterChange({ showOk: mode === 'uniform', submitting });
  }, [active, mode, submitting, onFooterChange]);

  useImperativeHandle(ref, () => ({
    /** 校验必填文本后提交；部分失败保留输入，成功关闭时由父弹窗刷新。 */
    submit: async () => {
      if (mode !== 'uniform' || submitting) return false;
      try {
        const values = await form.validateFields();
        const selected = filterValidCustomers(customers);
        if (!selected.length) throw new Error('请选择客户');
        if (selected.length > 200)
          throw new Error('单次最多修改200个客户，请分批操作');
        setSubmitting(true);
        setResult(undefined);
        const response = await batchReplaceCustomerContacts(
          selected.map((customer) => ({
            customerId: String(customer.id),
            contacts: [
              {
                contactName: values.contactName.trim(),
                contactMethod: values.contactMethod.trim(),
              },
            ],
          })),
        );
        if (!response.success || !response.result)
          throw new Error(response.message || '批量替换客户联系人失败');
        setResult(response.result);
        if (response.result.successCount > 0) onDirty();
        if (response.result.failedCount > 0) {
          message.warning(
            `成功 ${response.result.successCount} 个客户，失败 ${response.result.failedCount} 个客户`,
          );
          return false;
        }
        message.success(
          `已替换 ${response.result.successCount} 个客户的联系人`,
        );
        return true;
      } catch (error) {
        if (error && typeof error === 'object' && 'errorFields' in error)
          return false;
        message.error(
          error instanceof Error ? error.message : '批量替换客户联系人失败',
        );
        return false;
      } finally {
        setSubmitting(false);
      }
    },
  }));

  return (
    <Tabs
      activeKey={mode}
      onChange={setMode}
      items={[
        {
          key: 'uniform',
          label: '统一修改',
          children: (
            <>
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="批量修改客户联系人"
                description="每个客户配置一位客户联系人。将勾选客户的联系人姓名和联系方式统一修改为下方内容，我方联系人及登录账号不变。"
              />
              <Form form={form} layout="vertical" disabled={submitting}>
                <Form.Item
                  label="联系人姓名"
                  name="contactName"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: '请输入联系人姓名',
                    },
                    {
                      transform: (value: string) => value?.trim(),
                      max: 100,
                      message: '姓名不能超过100字符',
                    },
                  ]}
                >
                  <Input placeholder="请输入联系人姓名" />
                </Form.Item>
                <Form.Item
                  label="联系方式"
                  name="contactMethod"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: '请输入联系方式',
                    },
                    {
                      transform: (value: string) => value?.trim(),
                      max: 100,
                      message: '联系方式不能超过100字符',
                    },
                  ]}
                >
                  <Input placeholder="请输入手机号、固话等联系方式" />
                </Form.Item>
              </Form>
              {result && result.failedCount > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  message={`成功 ${result.successCount} 个客户，失败 ${result.failedCount} 个客户`}
                  description={
                    <List
                      size="small"
                      dataSource={result.failedItems}
                      renderItem={(failure) => (
                        <List.Item>
                          客户 {failure.customerId}：{failure.reason}
                        </List.Item>
                      )}
                    />
                  }
                />
              )}
            </>
          ),
        },
        {
          key: 'excel',
          label: 'Excel 导入',
          children: (
            <ConfigExcelImportPanel
              {...CUSTOMER_CONFIG_EXCEL.customerContact}
              description="每个客户配置一位客户联系人，请为每个客户ID填写一行姓名和联系方式。客户ID重复或该行校验失败时，该客户不修改。导入范围以文件中的客户ID为准，不受列表勾选范围限制；客户ID及联系方式须按文本填写。"
              onSuccess={onDirty}
            />
          ),
        },
      ]}
    />
  );
});

CustomerContactBatchPanel.displayName = 'CustomerContactBatchPanel';
export default CustomerContactBatchPanel;
